import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import SearchableSelect from '../ui/SearchableSelect';
import hierarchyService, {
  type HierarchyNode,
  type HierarchyTemplate,
} from '../../services/hierarchy.service';

/**
 * Per-level picker over the admin-defined filing hierarchy.
 *
 * One dropdown per hierarchy LEVEL (template in the Section → Sub-Section
 * chain), all visible immediately — labels come from the template names, so
 * when the admin adds/renames/re-nests levels in the Admin module this picker
 * follows with zero code changes. Child levels are enabled once their parent
 * is selected and only offer children of that parent.
 *
 * The backend scopes options by the user's node-scoped RBAC: nodes inside
 * their subtrees plus ancestors (for navigation only — the final upload
 * permission is checked against the selected node).
 */

interface HierarchyNodePickerProps {
  /** Which portal permission scopes the offered nodes. */
  permission?: 'view' | 'upload';
  /** Deepest selected node id (or null). */
  onChange: (nodeId: number | null) => void;
  /** Full selected chain, root-most first (e.g. [sectionId, subSectionId]). */
  onPathChange?: (nodeIds: number[]) => void;
  disabled?: boolean;
  required?: boolean;
  /** Notified after the templates load: whether a filing hierarchy exists. */
  onAvailability?: (hasNodes: boolean) => void;
}

const unwrapNodes = (res: any): HierarchyNode[] => res?.data?.data ?? [];
const unwrapTemplates = (res: any): HierarchyTemplate[] => res?.data?.data ?? [];

/** One level's dropdown; fetches its own options (hooks-per-level safe). */
const LevelDropdown: React.FC<{
  template: HierarchyTemplate;
  levelIndex: number;
  parentSelected: string | null; // '' = parent not chosen yet; null = root level
  parentName?: string;
  value: string;
  onSelect: (value: string) => void;
  permission: 'view' | 'upload';
  disabled: boolean;
  required: boolean;
}> = ({ template, levelIndex, parentSelected, parentName, value, onSelect, permission, disabled, required }) => {
  const isRoot = levelIndex === 0;
  const parentId = parentSelected ? Number(parentSelected) : undefined;
  const enabled = isRoot || !!parentSelected;

  const nodesQuery = useQuery({
    queryKey: ['hierarchy-level-nodes', template.id, parentId ?? 'all', permission],
    queryFn: () =>
      hierarchyService.getNodes({
        template_id: template.id,
        ...(parentId != null ? { parent_node_id: parentId } : {}),
        permission,
      }),
    enabled,
  });
  const options = unwrapNodes(nodesQuery.data).map((n) => ({
    id: String(n.id),
    name: n.label || `#${n.id}`,
  }));

  return (
    <div>
      <label className="block text-base font-semibold mb-1">
        {template.name}
        {required && <span className="text-error"> *</span>}
      </label>
      <SearchableSelect
        options={options}
        value={value}
        onChange={onSelect}
        placeholder={
          enabled
            ? `Select ${template.name.toLowerCase()}`
            : `Select ${(parentName || 'the level above').toLowerCase()} first`
        }
        loading={enabled && nodesQuery.isLoading}
        disabled={disabled || !enabled}
        valueKey="id"
      />
    </div>
  );
};

const HierarchyNodePicker: React.FC<HierarchyNodePickerProps> = ({
  permission = 'upload',
  onChange,
  onPathChange,
  disabled = false,
  required = false,
  onAvailability,
}) => {
  // Selection per level index ('' = none yet).
  const [selections, setSelections] = useState<string[]>([]);

  const templatesQuery = useQuery({
    queryKey: ['hierarchy-templates'],
    queryFn: () => hierarchyService.getTemplates(),
  });
  const levels = unwrapTemplates(templatesQuery.data);

  useEffect(() => {
    if (templatesQuery.isSuccess) onAvailability?.(levels.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatesQuery.isSuccess, levels.length]);

  const handleSelect = (levelIndex: number, value: string) => {
    // Record the selection and clear everything deeper.
    const next = levels.map((_, i) =>
      i < levelIndex ? selections[i] ?? '' : i === levelIndex ? value : ''
    );
    setSelections(next);

    // The selected chain = contiguous selections from the root.
    const chain: number[] = [];
    for (const v of next) {
      if (!v) break;
      chain.push(Number(v));
    }
    onChange(chain.length ? chain[chain.length - 1] : null);
    onPathChange?.(chain);
  };

  if (templatesQuery.isSuccess && levels.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {levels.map((template, i) => (
        <LevelDropdown
          key={template.id}
          template={template}
          levelIndex={i}
          parentSelected={i === 0 ? null : selections[i - 1] ?? ''}
          parentName={i === 0 ? undefined : levels[i - 1]?.name}
          value={selections[i] ?? ''}
          onSelect={(val) => handleSelect(i, val)}
          permission={permission}
          disabled={disabled}
          required={required}
        />
      ))}
    </div>
  );
};

export default HierarchyNodePicker;
